import React from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  formatCryptoDepositDate,
  formatMoney,
  formatUsdt,
  getCryptoDepositStatus,
  getCryptoMinDeposit,
  getCryptoWalletAddress,
  shortenTransactionHash,
  toNumber,
} from "./financeWallet.helpers";

export const UsdtDepositPanel = ({
  activeUsdtRate,
  cryptoLoading,
  cryptoWallet,
  cryptoWalletError,
  onCopyAddress,
  onRetryWallet,
}) => {
  const cryptoAddress = getCryptoWalletAddress(cryptoWallet);
  const cryptoMinDeposit = getCryptoMinDeposit(cryptoWallet);

  if (cryptoLoading) {
    return (
      <div className="wallet-usdt-empty" aria-live="polite">
        개인 USDT 입금 주소를 불러오는 중입니다.
      </div>
    );
  }

  return (
    <div className="wallet-usdt-panel">
      <div className="wallet-usdt-panel-head">
        <div>
          <span>USDT 입금 주소</span>
          <strong>TRC-20 네트워크</strong>
        </div>
        {activeUsdtRate > 0 ? <em>1 USDT = {formatMoney(activeUsdtRate)} 원</em> : null}
      </div>

      {cryptoAddress ? (
        <>
          <div className="wallet-usdt-address">
            <label htmlFor="wallet-usdt-address">입금 주소</label>
            <div className="wallet-copyline">
              <input id="wallet-usdt-address" type="text" value={cryptoAddress} readOnly />
              <button type="button" onClick={() => onCopyAddress(cryptoAddress)}>
                복사
              </button>
            </div>
          </div>
          <div className="wallet-usdt-qr">
            <QRCodeSVG
              value={cryptoAddress}
              size={112}
              level="M"
              title="개인 USDT 입금 주소 QR 코드"
            />
          </div>
        </>
      ) : (
        <div className="wallet-usdt-empty is-error" role="status">
          <strong>개인 USDT 입금 주소를 표시할 수 없습니다.</strong>
          <span>
            {cryptoWalletError ||
              "개인 입금 주소가 할당되지 않았습니다. 잠시 후 다시 시도해주세요."}
          </span>
          <button
            type="button"
            className="wallet-secondary-button"
            onClick={onRetryWallet}
            disabled={cryptoLoading}
          >
            다시 시도
          </button>
        </div>
      )}

      <div className="wallet-usdt-warning">
        <span>표시된 개인 주소로 입금하면 확인 후 자동 반영됩니다. 별도의 입금 신청은 필요하지 않습니다.</span>
        <span>TRC-20 네트워크만 지원합니다.</span>
        {cryptoMinDeposit > 0 ? (
          <span>최소 입금액: {formatMoney(cryptoMinDeposit)} USDT</span>
        ) : null}
        <span>다른 네트워크로 전송된 입금은 복구가 어려울 수 있습니다.</span>
      </div>
    </div>
  );
};

export const CryptoDepositHistory = ({ historyRows, historySkeletonRows, isLoading }) => (
  <div className="wallet-history-table is-crypto">
    <div className="wallet-history-row wallet-crypto-history-row is-head">
      <span>시간</span>
      <span>USDT</span>
      <span>환율</span>
      <span>KRW</span>
      <span>컨펌</span>
      <span>TX</span>
      <span>상태</span>
    </div>

    {isLoading ? (
      Array.from({ length: historySkeletonRows }).map((_, index) => (
        <div
          className="wallet-history-row wallet-crypto-history-row is-skeleton"
          role="status"
          aria-label="내역 불러오는 중"
          key={`wallet-crypto-skeleton-${index}`}
        >
          {Array.from({ length: 7 }).map((__, cellIndex) => (
            <i key={`wallet-crypto-skeleton-${index}-${cellIndex}`} />
          ))}
        </div>
      ))
    ) : historyRows.length ? (
      historyRows.map((row, index) => {
        const status = getCryptoDepositStatus(row);
        const transactionHash = String(row?.tx_hash || row?.txHash || "");
        return (
          <div
            className="wallet-history-row wallet-crypto-history-row"
            key={row?.id || row?._id || transactionHash || index}
          >
            <span data-label="시간">{formatCryptoDepositDate(row)}</span>
            <span data-label="USDT">{formatUsdt(row?.amount_usdt)}</span>
            <span data-label="환율">{formatMoney(row?.exchange_rate)}원</span>
            <span data-label="KRW">{formatMoney(row?.amount_krw)}원</span>
            <span data-label="컨펌">{Math.max(0, toNumber(row?.confirmations))}</span>
            <span
              className="wallet-crypto-tx"
              data-label="TX"
              title={transactionHash || undefined}
              aria-label={transactionHash ? `거래 해시 ${transactionHash}` : "거래 해시 없음"}
            >
              {shortenTransactionHash(transactionHash)}
            </span>
            <span data-label="상태" className={status.className}>
              {status.label}
            </span>
          </div>
        );
      })
    ) : (
      <div className="wallet-empty-history">표시할 내역이 없습니다.</div>
    )}
  </div>
);
